import _ from "lodash";

import { get, post } from "@/vue/api/api";
import { apiErrorHandler, oktaAxios as axios } from "@/vue/api/config";
import { augmentSettledPromiseResults } from "@/vue/utils/api/settledPromises";

import {
  SMS_FRAUD_WHITELIST_URL,
  SMS_DIAGNOSTICS_URL,
  SMS_SPAM_REPORTS_URL,
} from "../urls";
import { getEnv } from "@/env";

const whitelistRemovePromise = (customerData, reason) => {
  if (!customerData?.customerId) {
    throw new Error("Cannot find 'customerId' in submitted data.");
  }

  const payload = { removedNotes: reason };
  return axios.delete(
    `${SMS_FRAUD_WHITELIST_URL}/${customerData?.customerId}`,
    { data: payload }
  );
};

const whitelistForceAddPromise = (customerId, { force, reason }) => {
  const payload = reason ? { customerId, addedNotes: reason } : { customerId };
  const options = force ? { params: { force } } : null;

  return options
    ? axios.post(SMS_FRAUD_WHITELIST_URL, payload, options)
    : axios.post(SMS_FRAUD_WHITELIST_URL, payload);
};

/**
 * Takes in the current whitelist and a list of updated customers.
 * Performs the updates and returns the updated whitelist.
 * @param currentWhitelist whitelist as present in the state
 * @param updatedCustomers list of customers whose attributes have changed
 * @return {Array<Object>} updated whitelist
 */
const replaceCustomers = (currentWhitelist, updatedCustomers) => {
  const updatedWhitelist = [...currentWhitelist];
  const customerMap = updatedCustomers.reduce((accumulator, customer) => {
    accumulator[customer.customerId] = customer;
    return accumulator;
  }, {});

  for (let index = 0; index < currentWhitelist.length; index++) {
    const customerId = currentWhitelist[index].customerId;
    if (Object.keys(customerMap).includes(customerId)) {
      // Replace the customer data entirely in the collection
      updatedWhitelist[index] = customerMap[customerId];
    }
  }

  return updatedWhitelist;
};

export const smsaudit = {
  state: {
    smsDiagnostics: [],
    smsSpamReports: [],
    viewedSmsMessages: [],
    whitelistedCustomers: [],
    shouldRefetchDiagnostics: null,
  },
  getters: {
    getSmsDiagnostics: (state) => state.smsDiagnostics,
    getSpamReports: (state) => state.smsSpamReports,
    viewedSmsMessages: (state) => state.viewedSmsMessages,
    getWhitelistedCustomers: (state) => state.whitelistedCustomers,
    smsMessage: (state) => (smsId) =>
      _.find(state.viewedSmsMessages, { smsId }),
  },
  mutations: {
    SET_SMS_DIAGNOSTICS: (state, payload) => {
      state.smsDiagnostics = payload;
    },
    SET_VIEWED_SMS_MESSAGES: (state, payload) => {
      state.viewedSmsMessages = payload;
    },
    ADD_VIEWED_SMS_MESSAGE: (state, smsMessage) => {
      state.viewedSmsMessages.push(smsMessage);
    },
    SET_SMS_SPAM_REPORTS: (state, payload) => {
      state.smsSpamReports = payload;
    },
    SET_WHITELISTED_CUSTOMERS: (state, payload) => {
      state.whitelistedCustomers = payload;
    },
    SET_SHOULD_REFETCH_DIAGNOSTICS: (state, value) => {
      state.shouldRefetchDiagnostics = value;
    },
  },
  actions: {
    setShouldRefetchDiagnostics: ({ commit }, value) => {
      commit("SET_SHOULD_REFETCH_DIAGNOSTICS", value);
    },
    async fetchSmsDiagnostics(
      { commit },
      { startTime, stopTime, duplicateThreshold }
    ) {
      const body = {
        startTimestamp: startTime,
        endTimestamp: stopTime,
        duplicateThreshold,
      };

      const response = await axios
        .post(SMS_DIAGNOSTICS_URL, body)
        .catch(apiErrorHandler);
      const data = response?.data;
      if (!data?.length) {
        commit("SET_SMS_DIAGNOSTICS", []);
        return data;
      }

      commit("SET_SMS_DIAGNOSTICS", data);
      return data;
    },
    async fetchWhitelistedCustomers({ commit }) {
      const response = await axios
        .get(SMS_FRAUD_WHITELIST_URL)
        .catch(apiErrorHandler);
      const data = response?.data;
      if (!data?.length) {
        commit("SET_WHITELISTED_CUSTOMERS", []);
        return data;
      }

      commit("SET_WHITELISTED_CUSTOMERS", data);
      return data;
    },
    async whitelistCustomerById({ commit, getters }, customerId) {
      const response = await axios
        .post(SMS_FRAUD_WHITELIST_URL, { customerId })
        .catch(apiErrorHandler);
      // Add the customer to the list if valid to prevent an extra fetch
      if (response.status === 200) {
        const customer = response.data;
        const currentWhitelist = getters.getWhitelistedCustomers;
        const updatedWhitelist = [...currentWhitelist, customer];

        commit("SET_WHITELISTED_CUSTOMERS", updatedWhitelist);
      }
    },
    async removeFromWhitelist({ commit, getters }, { customers, reason }) {
      const results = await Promise.allSettled(
        customers.map((customerData) =>
          whitelistRemovePromise(customerData, reason)
        )
      );
      const augmentedResults = augmentSettledPromiseResults(results);
      const updatedWhitelist = replaceCustomers(
        getters.getWhitelistedCustomers,
        augmentedResults.responses.map((response) => response.data)
      );

      commit("SET_WHITELISTED_CUSTOMERS", updatedWhitelist);
      return augmentedResults;
    },
    async whitelistMultipleCustomers({ commit, getters }, parameters) {
      const { customerIds, force, reason } = parameters;
      const results = await Promise.allSettled(
        customerIds.map((customerId) =>
          whitelistForceAddPromise(customerId, { force, reason })
        )
      );
      const augmentedResults = augmentSettledPromiseResults(results);
      const updatedWhitelist = replaceCustomers(
        getters.getWhitelistedCustomers,
        augmentedResults.responses.map((response) => response.data)
      );

      commit("SET_WHITELISTED_CUSTOMERS", updatedWhitelist);
      return augmentedResults;
    },
    async getSmsBody(
      { commit, dispatch, getters },
      { smsId, customerId, phoneNumber, numberOfDuplicatedTexts }
    ) {
      const alreadyViewed = getters.smsMessage(smsId);
      if (alreadyViewed) return alreadyViewed;

      const smsBody = await dispatch("fetchSmsBody", { smsId });
      if (!smsBody) {
        throw new Error(`Could not fetch SMS:${smsId}`);
      }
      const smsMessage = { smsId, smsBody };

      commit("ADD_VIEWED_SMS_MESSAGE", smsMessage);

      // send sms message response THEN set current customer and set audit trail
      (async () => {
        await dispatch("findAndFetchCustomer", {
          searchField: "customerId",
          searchTerm: customerId,
        });
        dispatch("sendAudit", {
          eventType: "READ",
          objectType: "SMS Audit",
          objectKey: smsId,
          details: { phoneNumber, numberOfDuplicatedTexts },
        });
      })().then();

      return smsMessage;
    },
    async fetchSmsBody(context, { smsId }) {
      const data = {
        queryName: "smsbody",
        queryArguments: [smsId],
      };
      const responseData = await post(
        getEnv("VUE_APP_API_URL_DB") + "/v1/db/get",
        data
      );
      if (!responseData || !responseData.length) {
        return null;
      }
      return responseData[0].body;
    },
    async fetchSmsSpamReports({ commit }) {
      const data = await get(SMS_SPAM_REPORTS_URL);
      commit("SET_SMS_SPAM_REPORTS", data);
      return data;
    },
  },
};
